/**
 * @fileoverview Reusable column visibility toggle for TanStack React Table.
 *
 * Mirrors the Precedent Library's "Columns 7/10" popover pattern,
 * adapted to work with TanStack's `VisibilityState` API. Each column
 * opt-in via `meta.label` — columns without a label are always visible
 * and don't appear in the menu.
 *
 * Used by: Job Contacts table, Firm Contacts table
 */

import { useState } from "react";
import type { Table } from "@tanstack/react-table";
import { Check, Columns3 } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

interface ColumnVisibilityMenuProps<T> {
  table: Table<T>;
}

export function ColumnVisibilityMenu<T>({
  table,
}: ColumnVisibilityMenuProps<T>) {
  const [open, setOpen] = useState(false);

  const toggleableColumns = table
    .getAllColumns()
    .filter((col) => col.getCanHide() && col.columnDef.meta?.label);

  const visibleCount = toggleableColumns.filter((col) =>
    col.getIsVisible(),
  ).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs text-primary/70 transition hover:bg-secondary"
        >
          <Columns3 className="size-3.5" />
          Columns
          <Badge variant="secondary" className="ml-0.5 text-[10px]">
            {visibleCount}/{toggleableColumns.length}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {toggleableColumns.map((col) => {
          const visible = col.getIsVisible();
          return (
            <button
              key={col.id}
              type="button"
              onClick={() => col.toggleVisibility(!visible)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition hover:bg-secondary"
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded border",
                  visible
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background",
                )}
              >
                {visible && <Check className="size-3" />}
              </span>
              <span className="text-primary/80">
                {col.columnDef.meta?.label}
              </span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
